import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  deleteProduct,
  fetchProduct,
  fetchProductImageByIdUrl,
} from '../../api/product';
import {
  formatQty,
  formatWon,
  Product,
  formatUseYn,
  vatTypeLabel,
} from '../../types/product';
import ImageLightbox from '../common/ImageLightbox';
import ProductTabPlaceholder from './ProductTabPlaceholder';
import ProductPartsTab from './ProductPartsTab';
import ProductCertificatesTab from './ProductCertificatesTab';
import InventoryStockHistory from './InventoryStockHistory';
import ProductInspectionsTab from './ProductInspectionsTab';
import ProductTabBar from './ProductTabBar';
import ProductTagChips from './ProductTagChips';
import { ProductTabKey } from './productTabConfig';
import '../contract/ContractDetail.css';
import './ProductForm.css';

import { reportError } from '../../utils/notify';

interface ProductDetailProps {
  productId: number;
  onBack: () => void;
  onEdit: () => void;
  onDeleted: () => void;
}

function ProductDetail({ productId, onBack, onEdit, onDeleted }: ProductDetailProps) {
  const [product, setProduct] = useState<Product | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<number, string>>({});
  const [previewImageId, setPreviewImageId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<ProductTabKey>('general');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  useEffect(() => {
    setActiveTab('general');
    let cancelled = false;
    const created: string[] = [];
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchProduct(productId);
        if (cancelled) return;
        setProduct(data);
        const metas = data.images ?? [];
        const nextUrls: Record<number, string> = {};
        for (const meta of metas) {
          try {
            const url = await fetchProductImageByIdUrl(productId, meta.imageId);
            created.push(url);
            nextUrls[meta.imageId] = url;
          } catch {
            // skip broken
          }
        }
        if (cancelled) {
          created.forEach((url) => window.URL.revokeObjectURL(url));
          return;
        }
        setImageUrls((prev) => {
          Object.values(prev).forEach((url) => window.URL.revokeObjectURL(url));
          return nextUrls;
        });
        const primary = metas.find((m) => m.primary) ?? metas[0];
        setPreviewImageId(primary?.imageId ?? null);
        setError('');
      } catch {
        reportError(setError, '제품을 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
      created.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [productId]);

  const images = useMemo(() => product?.images ?? [], [product?.images]);
  const previewUrl = useMemo(() => {
    if (previewImageId != null && imageUrls[previewImageId]) {
      return imageUrls[previewImageId];
    }
    const primary = images.find((m) => m.primary) ?? images[0];
    return primary ? imageUrls[primary.imageId] ?? null : null;
  }, [previewImageId, imageUrls, images]);

  const handleDelete = async () => {
    if (!product) return;
    const ok = window.confirm(`'${product.productName}' 제품을 삭제할까요?`);
    if (!ok) return;
    try {
      setDeleting(true);
      await deleteProduct(product.productId);
      onDeleted();
    } catch {
      reportError(setError, '제품 삭제에 실패했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <p className="contract-detail__hint">불러오는 중...</p>;
  }

  if (!product) {
    return (
      <div className="contract-detail">
        <p className="contract-detail__error">{error || '제품을 찾을 수 없습니다.'}</p>
        <button type="button" onClick={onBack}>
          목록으로
        </button>
      </div>
    );
  }

  const stockLow =
    (product.safetyStock ?? 0) > 0 && (product.stockQty ?? 0) <= (product.safetyStock ?? 0);

  return (
    <div className="contract-detail">
      <header className="contract-detail__header">
        <div className="contract-detail__header-top">
          <div className="product-detail__hero">
            <div className="product-detail__gallery">
              <div
                className={`product-detail__thumb product-detail__thumb--lg${
                  previewUrl ? ' image-lightbox-trigger' : ''
                }`}
                role={previewUrl ? 'button' : undefined}
                tabIndex={previewUrl ? 0 : undefined}
                title={previewUrl ? '클릭하여 확대' : undefined}
                onClick={() => {
                  if (previewUrl) setLightboxOpen(true);
                }}
                onKeyDown={(e) => {
                  if (!previewUrl) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxOpen(true);
                  }
                }}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt={product.productName} />
                ) : (
                  <span>사진 없음</span>
                )}
              </div>
              {images.length > 1 && (
                <div className="product-form__thumbs product-detail__thumbs">
                  {images.map((meta) => {
                    const url = imageUrls[meta.imageId];
                    if (!url) return null;
                    return (
                      <button
                        key={meta.imageId}
                        type="button"
                        className={[
                          'product-form__thumb',
                          previewImageId === meta.imageId ? 'is-active' : '',
                          meta.primary ? 'is-primary' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => setPreviewImageId(meta.imageId)}
                      >
                        <img src={url} alt="" />
                        {meta.primary && <span className="product-form__thumb-badge">대표</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <div className="product-detail__badges">
                <span>{product.category || '미분류'}</span>
                <span>{product.unit || 'EA'}</span>
                <span>{vatTypeLabel(product.vatType)}</span>
                <span className={product.useYn === 'N' ? 'is-off' : ''}>
                  {formatUseYn(product.useYn)}
                </span>
                {stockLow && <span className="is-low">재고부족</span>}
              </div>
              {(product.tags?.length ?? 0) > 0 && (
                <div className="product-detail__tags">
                  <ProductTagChips tags={product.tags ?? []} compact />
                </div>
              )}
              <h2>{product.productName}</h2>
              <p>{product.productCode || '제품코드 없음'}</p>
            </div>
          </div>
          <div className="contract-detail__actions">
            <button type="button" onClick={onEdit}>
              수정
            </button>
            <button type="button" className="is-danger" disabled={deleting} onClick={handleDelete}>
              {deleting ? '삭제 중...' : '삭제'}
            </button>
            <button type="button" className="is-ghost" onClick={onBack}>
              목록으로
            </button>
          </div>
        </div>
      </header>

      {error && <p className="contract-detail__error">{error}</p>}

      <ProductTabBar activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'general' ? (
        <div className="contract-detail__grid">
          <section className="contract-detail__card">
            <h3>기본 정보</h3>
            <dl>
              <div>
                <dt>제품코드</dt>
                <dd>{product.productCode || '-'}</dd>
              </div>
              <div>
                <dt>분류</dt>
                <dd>{product.category || '-'}</dd>
              </div>
              <div>
                <dt>재고연동</dt>
                <dd>
                  {product.category === '완제품'
                    ? product.inventoryId
                      ? `재고 #${product.inventoryId}`
                      : '미연동'
                    : '-'}
                </dd>
              </div>
              <div>
                <dt>규격</dt>
                <dd>{product.spec || '-'}</dd>
              </div>
              <div>
                <dt>바코드</dt>
                <dd>{product.barcode || '-'}</dd>
              </div>
              <div>
                <dt>제조사/공급처</dt>
                <dd>{product.manufacturer || '-'}</dd>
              </div>
              <div>
                <dt>단위</dt>
                <dd>{product.unit || 'EA'}</dd>
              </div>
              <div>
                <dt>과세구분</dt>
                <dd>{vatTypeLabel(product.vatType)}</dd>
              </div>
              <div>
                <dt>사용여부</dt>
                <dd>{formatUseYn(product.useYn)}</dd>
              </div>
            </dl>
          </section>

          <section className="contract-detail__card">
            <h3>가격 · 재고</h3>
            <dl>
              <div>
                <dt>원가</dt>
                <dd>{formatWon(product.costPrice)}</dd>
              </div>
              <div>
                <dt>공급단가</dt>
                <dd>{formatWon(product.supplyPrice)}</dd>
              </div>
              <div>
                <dt>현재고</dt>
                <dd className={stockLow ? 'product-panel__stock-low' : undefined}>
                  {formatQty(product.stockQty)}
                </dd>
              </div>
              <div>
                <dt>안전재고</dt>
                <dd>{formatQty(product.safetyStock)}</dd>
              </div>
              <div>
                <dt>창고위치</dt>
                <dd>{product.storageLocation || '-'}</dd>
              </div>
              <div>
                <dt>등록자</dt>
                <dd>{product.createdUserName || '-'}</dd>
              </div>
              <div>
                <dt>수정자</dt>
                <dd>{product.lastUpdatedUserName || '-'}</dd>
              </div>
            </dl>
          </section>

          <section className="contract-detail__card is-full">
            <h3>해시태그</h3>
            <ProductTagChips tags={product.tags ?? []} />
          </section>

          <section className="contract-detail__card is-full">
            <h3>비고</h3>
            <p className="contract-detail__memo">{product.memo || '-'}</p>
          </section>
        </div>
      ) : activeTab === 'parts' ? (
        <ProductPartsTab productId={product.productId} />
      ) : activeTab === 'certificates' ? (
        <ProductCertificatesTab productId={product.productId} />
      ) : activeTab === 'inspections' ? (
        <ProductInspectionsTab
          inventoryId={product.inventoryId}
          itemKind="FINISHED"
          emptyHint="이 제품에 연동된 재고 품목이 없습니다. 분류가 완제품이고 재고관리와 연결되면 검사이력이 표시됩니다."
        />
      ) : activeTab === 'stockHistory' ? (
        <InventoryStockHistory
          inventoryId={product.inventoryId}
          unit={product.unit}
          emptyHint="이 제품에 연동된 재고 품목이 없습니다. 분류가 완제품이고 재고관리와 연결되면 이력이 표시됩니다."
        />
      ) : (
        <ProductTabPlaceholder tab={activeTab} />
      )}
      <ImageLightbox
        src={lightboxOpen ? previewUrl : null}
        alt={product.productName}
        onClose={closeLightbox}
      />
    </div>
  );
}

export default ProductDetail;
